import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { theme } from '../styles/theme';
import Card from '../components/common/Card';
import { userAPI, certificateUserAPI } from '../utils/api';
import { Users, FileText, CheckCircle, AlertCircle, Upload, Settings } from 'react-feather';
import { toast } from 'react-toastify';

const DashboardContainer = styled.div`
  display: grid;
  gap: ${theme.spacing.xl};
  padding: ${theme.spacing.lg};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.lg};
`;

const StatCard = styled(Card)`
  text-align: center;
  padding: ${theme.spacing.lg};
  transition: all 0.2s ease-in-out;
  position: relative;
  overflow: hidden;
  height: 100%;
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.lg};
  }

  h3 {
    color: ${theme.colors.text.secondary};
    margin-bottom: ${theme.spacing.sm};
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing.sm};
  }
  
  .value {
    font-size: 2rem;
    font-weight: 600;
    color: ${props => theme.colors[props.color || 'primary']};
    margin-bottom: ${theme.spacing.sm};
  }

  .description {
    color: ${theme.colors.text.secondary};
    font-size: 0.875rem;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
`;

const ActionCard = styled(Card)`
  text-align: center;
  padding: ${theme.spacing.lg};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.lg};
  }
  
  h3 {
    color: ${theme.colors.text.primary};
    margin-bottom: ${theme.spacing.md};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing.sm};
  }
  
  p {
    color: ${theme.colors.text.secondary};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Skeleton = styled.div`
  background: linear-gradient(
    90deg,
    ${theme.colors.background} 0%,
    ${theme.colors.surface} 50%,
    ${theme.colors.background} 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: ${theme.borderRadius.md};

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const StatSkeleton = styled(Skeleton)`
  height: 24px;
  width: 80%;
  margin: 0 auto ${theme.spacing.sm};

  &.title {
    width: 60%;
    height: 20px;
  }

  &.value {
    width: 40%;
    height: 40px;
  }

  &.description {
    width: 70%;
    height: 16px;
  }
`;

const ActionSkeleton = styled(Skeleton)`
  height: 24px;
  width: 70%;
  margin: 0 auto ${theme.spacing.sm};

  &.description {
    width: 80%;
    height: 16px;
  }
`;

const StatCardContent = ({ title, value, description, icon: Icon, color, loading }) => {
  if (loading) {
    return (
      <>
        <StatSkeleton className="title" />
        <StatSkeleton className="value" />
        <StatSkeleton className="description" />
      </>
    );
  }

  return (
    <>
      <h3>
        <Icon />
        {title}
      </h3>
      <div className="value">{value}</div>
      <div className="description">{description}</div>
    </>
  );
};

const ActionCardContent = ({ title, description, icon: Icon, loading }) => {
  if (loading) {
    return (
      <>
        <ActionSkeleton />
        <ActionSkeleton className="description" />
      </>
    );
  }

  return (
    <>
      <h3>
        <Icon />
        {title}
      </h3>
      <p>{description}</p>
    </>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    offerLetterSentCount: 0,
    completionLetterSentCount: 0,
    pendingOfferLetters: 0,
    pendingCompletionLetters: 0
  });
  const [certStats, setCertStats] = useState({
    totalUsers: 0,
    offerLetterSentCount: 0,
    completionLetterSentCount: 0,
    pendingOfferLetters: 0,
    pendingCompletionLetters: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await userAPI.getUserCounts();
        setStats(data);
        const certData = await certificateUserAPI.getCounts();
        setCertStats(certData);
      } catch (error) {
        toast.error('Failed to fetch statistics');
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <DashboardContainer>
      <h1>Dashboard</h1>
      <h4 className='d-flex align-items-center gap-2'>
       <p>Total Students:</p>  <p className='fw-bold'> {stats.totalUsers}</p>
      </h4>
      <StatsGrid>
        {/* <StatCard color="primary">
          <StatCardContent
            loading={loading}
            icon={Users}
            title="Total Students"
            value={stats.totalUsers}
            description="Total registered students"
          />
        </StatCard> */}
        
        <Link to="/users/offer-letter-sent" style={{ textDecoration: 'none' }}>
          <StatCard color="success">
            <StatCardContent
              loading={loading}
              icon={CheckCircle}
              title="Offer Letters Sent"
              value={stats.offerLetterSentCount}
              description="Successfully sent offer letters"
            />
          </StatCard>
        </Link>

        <Link to="/users/offer-letter-remaining" style={{ textDecoration: 'none' }}>
          <StatCard color="warning">
            <StatCardContent
              loading={loading}
              icon={AlertCircle}
              title="Pending Offer Letters"
              value={stats.pendingOfferLetters}
              description="Offer letters to be sent"
            />
          </StatCard>
        </Link>
        
        <Link to="/users/complection-sent" style={{ textDecoration: 'none' }}>
          <StatCard color="info">
            <StatCardContent
              loading={loading}
              icon={CheckCircle}
              title="Completion Letters Sent"
              value={stats.completionLetterSentCount}
              description="Completed internships"
            />
          </StatCard>
        </Link>

        <Link to="/users/complection-remaining" style={{ textDecoration: 'none' }}>
          <StatCard color="error">
            <StatCardContent
              loading={loading}
              icon={AlertCircle}
              title="Pending Completion Letters"
              value={stats.pendingCompletionLetters}
              description="Completion letters to be sent"
            />
          </StatCard>
        </Link>
      </StatsGrid>
      
      <h2>Certificate Users</h2>

      <h3>Total Students: {certStats?.totalUsers}</h3>
      <StatsGrid>
        <Link to="/certificate-users" style={{ textDecoration: 'none' }}>
          <StatCard color="success">
            <StatCardContent
              loading={loading}
              icon={CheckCircle}
              title="Certificates Sent"
              value={certStats.completionLetterSentCount}
              description="Successfully sent certificates"
            />
          </StatCard>
        </Link>

        <Link to="/certificate-users" style={{ textDecoration: 'none' }}>
          <StatCard color="warning">
            <StatCardContent
              loading={loading}
              icon={AlertCircle}
              title="Pending Certificates"
              value={certStats.pendingCompletionLetters}
              description="Certificates to be sent"
            />
          </StatCard>
        </Link>
      </StatsGrid>

      <h2>Manage Students</h2>
      <QuickActions>
        <Link to="/users" style={{ textDecoration: 'none' }}>
          <ActionCard>
            <ActionCardContent
              loading={loading}
              icon={Users}
              title="Internship + Training"
              description="View all students"
            />
          </ActionCard>
        </Link>

        <Link to="/certificate-users" style={{ textDecoration: 'none' }}>
          <ActionCard>
            <ActionCardContent
              loading={loading}
              icon={Users}
              title="Certificate Users"
              description="Manage certificate users"
            />
          </ActionCard>
        </Link>

        {/* <Link to="/users/offer-letter-remaining" style={{ textDecoration: 'none' }}>
          <ActionCard>
            <ActionCardContent
              loading={loading}
              icon={AlertCircle}
              title="Pending Offer Letters"
              description={`${stats.pendingOfferLetters} offer letters pending`}
            />
          </ActionCard>
        </Link>

        <Link to="/users/complection-remaining" style={{ textDecoration: 'none' }}>
          <ActionCard>
            <ActionCardContent
              loading={loading}
              icon={AlertCircle}
              title="Pending Completion Letters"
              description={`${stats.pendingCompletionLetters} completion letters pending`}
            />
          </ActionCard>
        </Link>
         */}
        {/* <Link to="/upload" style={{ textDecoration: 'none' }}>
          <ActionCard>
            <ActionCardContent
              loading={loading}
              icon={Upload}
              title="Upload Data"
              description="Upload new student data via CSV"
            />
          </ActionCard>
        </Link> */}
      </QuickActions>
    </DashboardContainer>
  );
};

export default Dashboard; 